// Third Party
import {
  NextPage,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';
import { Session } from 'next-auth';

// Utils
import { auth } from '@auth';
import { routes, canonicalUrlForPath } from '@routes';
import {
  imperativeNodeListingBySlugQuery,
  ListingBySlugPartial,
} from '@utils/imperative-node-queries';
import { validateDates } from '@utils/date-helpers';

// UI
import { PublicLayout } from '@layouts/public-layout';
import { ListingShow } from '@pages-components/listing-show';
import { AnalyticsRef } from '@components/analytics-ref';
import { QuotesProvider } from '@pages-components/listing-show';
import { ReviewsProvider } from '@pages-components/listing-show/reviews-context';
import { QuoteComparisonProvider } from '@pages-components/listing-show/quotes-compare/quote-comparison-context';

type ListingPageProps = {
  session: Session | null;
  listing: ListingBySlugPartial;
  adultCount: number | null;
  childCount: number | null;
  infantCount: number | null;
  petCount: number | null;
  checkInDate: string | null;
  checkOutDate: string | null;
};

type UnauthenticatedRedirect = {
  redirect: {
    destination: string;
    permanent: false;
  };
};

function generateMetaTitle(listing: {
  name: string;
  city: string;
  stateOrProvinceLongName: string;
}): string {
  return `Visit ${listing.name} in ${listing.city}, ${listing.stateOrProvinceLongName} | Savvy`;
}

function generateMetaDescription(listing: {
  name: string;
  city: string;
  stateOrProvinceLongName: string;
}): string {
  return `Visit ${listing.name} in ${listing.city}, ${listing.stateOrProvinceLongName}`;
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<
  | {
      props: ListingPageProps;
    }
  | UnauthenticatedRedirect
  | { notFound: true }
> {
  const slug = context.query.slug;

  if (typeof slug !== 'string') throw new Error('slug must be a string');

  const adultCount = context.query?.adultCount
    ? parseInt(context.query?.adultCount as string)
    : 1;

  const childCount = context.query?.adultCount
    ? parseInt(context.query?.childCount as string)
    : 0;

  const infantCount = context.query?.adultCount
    ? parseInt(context.query?.infantCount as string)
    : 0;

  const petCount = context.query?.adultCount
    ? parseInt(context.query?.petCount as string)
    : 0;

  const checkInDate = context.query?.checkInDate
    ? (context.query?.checkInDate as string)
    : null;

  const checkOutDate = context.query?.checkOutDate
    ? (context.query?.checkOutDate as string)
    : null;

  const session = await auth(context);

  const result = await imperativeNodeListingBySlugQuery(
    {
      variables: {
        slug: slug,
      },
      errorPolicy: 'all',
    },

    session?.user?.accessToken || undefined
  );

  /**
   * The date validation is being performed to ensure that
   * the check-in and check-out dates provided by the user
   * are valid and in the future.
   */
  if (checkInDate && checkOutDate) {
    const isValid = validateDates(
      new Date(checkInDate),
      new Date(checkOutDate)
    );

    if (!isValid) {
      return {
        redirect: {
          destination: routes.listings({
            slug: encodeURIComponent(slug),
          }),
          permanent: false,
        },
      };
    }
  }

  if (result.data?.listingBySlug.__typename === 'Listing') {
    return {
      props: {
        listing: result.data?.listingBySlug,
        adultCount,
        childCount,
        infantCount,
        petCount,
        checkInDate,
        checkOutDate,
        session: session,
      },
    };
  } else {
    return { notFound: true };
  }
}

const ListingPage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
  listing,
  session,
  adultCount,
  childCount,
  infantCount,
  petCount,
  checkInDate,
  checkOutDate,
}) => {
  return (
    <PublicLayout
      session={session}
      loadingSession={false}
      canonicalUrl={canonicalUrlForPath(
        routes.listings({
          slug: encodeURIComponent(listing.slug),
        })
      )}
      pageDescription={generateMetaDescription({
        name: listing.name || '',
        city: listing.city || '',
        stateOrProvinceLongName: listing.stateOrProvinceLongName || '',
      })}
      pageTitle={generateMetaTitle({
        name: listing.name || '',
        city: listing.city || '',
        stateOrProvinceLongName: listing.stateOrProvinceLongName || '',
      })}
      noIndex={true}
      imageUrl={listing.galleryAssets[0]?.url}
    >
      <QuotesProvider
        listingSlug={listing.slug}
        guestCapacity={listing.guestCapacity}
        adultCount={adultCount}
        childCount={childCount}
        infantCount={infantCount}
        petCount={petCount}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
      >
        <QuoteComparisonProvider listing={listing}>
          <ReviewsProvider listingId={listing.id}>
            <AnalyticsRef name="listing_details">
              <ListingShow listing={listing} />
            </AnalyticsRef>
          </ReviewsProvider>
        </QuoteComparisonProvider>
      </QuotesProvider>
    </PublicLayout>
  );
};

export default ListingPage;
